import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/ProductsService';
import {Product} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';
import {productsKeys} from './useProductsQuery';

/**
 * FNP-918 (§6): build the mutation payload by WHITELISTING the fields the API's
 * Input type declares.
 *
 * It was previously built by spreading the record and `delete`-ing `created`
 * and `updated`. Two problems with that shape:
 *
 *   1. It is a DENYLIST. Every field the UI adds to the record is silently sent
 *      to the API until someone remembers to `delete` it too. A whitelist
 *      cannot leak a field nobody thought about.
 *   2. The same pattern one layer up (`delete record.created` on the caller's
 *      object) MUTATES the live Formik/React Query object — which
 *      ProductsService.update did literally, on the caller's record. See the
 *      note there.
 *
 * ⚠️ `name` is the ONLY field. The form collects only a name (FormComponent),
 * and `createProduct` / `updateProduct` select back only `id` and `name`. The
 * `url`, `logoUrl` and `description` fields exist on the TypeScript type but
 * are neither collected by the form nor returned by any query — sending them
 * would be forwarding whatever stale value happened to be on the object.
 */
const toProductInput = (values: Product) => ({
    name: values.name,
});

export const useProduct = (productId?: string) => {
    const queryClient = useQueryClient();

    const productQuery = useQuery({
        queryKey: productsKeys.detail(productId ?? ''),
        queryFn: () => fetch(productId!),
        enabled: !!productId,
    });

    const createMutation = useMutation({
        mutationFn: (values: Product) => add(toProductInput(values)),
        onSuccess: () => {
            notifySuccess('Product created');
            queryClient.invalidateQueries({queryKey: productsKeys.all});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: Product) => {
            if (!productId) throw new Error('productId is required parameter');
            return update(productId, {
                id: productId,
                ...toProductInput(values),
            } as Product);
        },
        onSuccess: () => {
            notifySuccess('Product updated');
            // Detail-page mutations invalidate BOTH the detail key and the
            // list key, or the list keeps showing the pre-edit name (§8).
            // `productsKeys.all` is the prefix of both.
            queryClient.invalidateQueries({queryKey: productsKeys.all});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!productId) throw new Error('productId is required parameter');
            return deleteRecord(productId);
        },
        onSuccess: () => {
            notifySuccess('Product deleted');
            queryClient.invalidateQueries({queryKey: productsKeys.all});
        },
        onError: notifyError,
    });

    return {
        record: productQuery.data,
        loading: productQuery.isLoading,
        fetchRecord: productQuery.refetch,
        // ⚠️ mutateAsync, NOT mutate (§6/§7).
        //
        // `mutate` is fire-and-forget: it returns void, so the drawer's
        // `await handleSubmit(values); onClose();` awaits `undefined` and
        // closes IMMEDIATELY. A server-side rejection then destroys the
        // operator's input and toasts into a closed drawer, and Formik clears
        // `isSubmitting` on the same tick so the submit button's `disabled`
        // never engages. `mutateAsync` returns the real promise, which makes
        // both the drawer's await and the disabled-while-submitting wiring do
        // what they already claim to do.
        //
        // ⚠️ The products EditPage passes `updateRecord` straight into
        // FormDrawer as `handleSubmit`, so this is the change that makes that
        // drawer's `await` meaningful.
        createRecord: createMutation.mutateAsync,
        updateRecord: updateMutation.mutateAsync,
        deleteRecord: deleteMutation.mutateAsync,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
